/**
 * REGISTERING INTEREST (`/register?from=<source>`, migration 0040) — before anyone has an account.
 *
 * Mounted before the sign-in gate (main.tsx), with no session call. Made for a phone opened from a QR code at an
 * event: one column, tall fields, the choices as taps. It asks what the person would like Leveri to do — it claims
 * nothing about what Leveri does — and says plainly what happens next: an invite code, by email, when their place
 * opens. The page's one saturated control is "Register"; a chosen option is marked by an ink border and a tick.
 *
 * Beside the form (above it on a phone), the capability reel shows a minute of the work itself, so a person deciding
 * whether to register sees what Leveri does rather than reading a promise about it.
 */
import { useState, type FormEvent } from "react";
import { ErrorEnvelope, INTEREST_FIRM_SIZES, INTEREST_ROLES, INTEREST_SOURCE, INTEREST_WANTS, RegisterInterestResponse } from "@leveri/contracts";
import { Button, Notice, Reading, TextField } from "../file/index.js";
import { CapabilityReel } from "../reel/reel.js";
import { Wordmark } from "./shell.js";

/** The event or campaign in the address (`?from=ican-2026`), or none. */
const sourceFromAddress = (): string | undefined => {
  const from = new URLSearchParams(window.location.search).get("from")?.trim().toLowerCase() ?? "";
  return INTEREST_SOURCE.test(from) ? from : undefined;
};

const TALL = "!h-11 !text-[15px]";

function Choice({ label, chosen, onPress }: { label: string; chosen: boolean; onPress: () => void }) {
  return (
    <button
      type="button" aria-pressed={chosen} onClick={onPress}
      className={`min-h-11 rounded-control border px-3.5 py-2 text-left text-[14px] leading-snug transition-colors ${chosen ? "border-ink bg-surface text-ink" : "border-line-strong bg-canvas text-ink-soft hover:border-ink"}`}
    >
      {chosen && <span aria-hidden className="mr-1.5">✓</span>}{label}
    </button>
  );
}

function Choices<K extends string>({ legend, hint, options, chosen, onToggle }: {
  legend: string; hint?: string; options: ReadonlyArray<{ key: K; label: string }>; chosen: ReadonlySet<K>; onToggle: (key: K) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm font-medium text-ink-soft">{legend}</legend>
      {hint && <p className="-mt-0.5 mb-2 text-[13px] text-stone">{hint}</p>}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => <Choice key={o.key} label={o.label} chosen={chosen.has(o.key)} onPress={() => onToggle(o.key)} />)}
      </div>
    </fieldset>
  );
}

type Want = (typeof INTEREST_WANTS)[number]["key"];
type Role = (typeof INTEREST_ROLES)[number]["key"];
type Size = (typeof INTEREST_FIRM_SIZES)[number]["key"];

export function RegisterPage() {
  const [source] = useState(sourceFromAddress);
  const [wants, setWants] = useState<ReadonlySet<Want>>(new Set());
  const [role, setRole] = useState<Role | null>(null);
  const [size, setSize] = useState<Size | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<{ name: string; email: string } | null>(null);

  const toggleWant = (key: Want) => setWants((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  async function register(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    const text = (name: string) => { const v = String(form.get(name) ?? "").trim(); return v ? v : undefined; };
    const body = {
      ...(source ? { source } : {}),
      fullName: text("fullName") ?? "", email: text("email") ?? "",
      phone: text("phone"), firmName: text("firmName"),
      ...(role ? { role } : {}), ...(size ? { firmSize: size } : {}),
      wants: [...wants], wantsOther: text("wantsOther"),
      consent: form.get("consent") === "on", website: text("website"),
    };
    setBusy(true);
    setFailure(null);
    try {
      const res = await fetch("/api/public/interest", {
        method: "POST", credentials: "omit", body: JSON.stringify(body),
        headers: { accept: "application/json", "content-type": "application/json" },
      });
      const payload: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const envelope = ErrorEnvelope.safeParse(payload);
        throw new Error(envelope.success ? envelope.data.error.message : "Something went wrong. Try again in a moment.");
      }
      RegisterInterestResponse.parse(payload);
      setDone({ name: body.fullName.split(/\s+/)[0] ?? body.fullName, email: body.email });
      window.scrollTo({ top: 0 });
    } catch (err) {
      setFailure(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-canvas px-5 py-10 text-ink sm:py-14">
      {/* One column on a phone (name, what this is, the reel, the form); on a wide screen the reel stands beside the form. */}
      <div className="mx-auto grid w-full max-w-[460px] gap-y-8 [grid-template-areas:'brand'_'intro'_'reel'_'form'] lg:max-w-[1080px] lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-x-16 lg:grid-rows-[auto_auto_1fr] lg:[grid-template-areas:'brand_brand'_'reel_intro'_'reel_form']">
        <div className="[grid-area:brand]"><Wordmark /></div>
        <div className="lg:sticky lg:top-10 lg:max-w-[560px] lg:self-start [grid-area:reel]">
          <p className="mb-2.5 text-[13px] text-stone">Leveri at work, in one minute</p>
          <CapabilityReel />
        </div>
        {done ? (
          <div data-testid="register-done" className="[grid-area:intro]">
            <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Thank you, {done.name}.</h1>
            <p className="mt-2 text-[15px] leading-relaxed text-ink-soft">
              You&rsquo;re on the list. When your place opens we&rsquo;ll email your invite code to <span className="text-ink">{done.email}</span>.
            </p>
            <button type="button" onClick={() => { setDone(null); setWants(new Set()); setRole(null); setSize(null); }}
              className="mt-6 text-sm text-ink underline underline-offset-2">Register a colleague</button>
          </div>
        ) : (
          <>
            <div className="[grid-area:intro]">
              <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Register your interest</h1>
              <p className="mt-1.5 text-[15px] leading-relaxed text-stone">
                Leveri is opening to accountants in waves. Tell us what you&rsquo;d like it to do for you, and we&rsquo;ll send your invite code when your place opens.
              </p>
            </div>
            <form onSubmit={register} className="flex flex-col gap-6 [grid-area:form]" data-testid="register-form">
              <TextField name="fullName" label="Your name" required autoComplete="name" maxLength={200} className={TALL} />
              <TextField name="email" type="email" label="Email" required autoComplete="email" maxLength={254} className={TALL} />
              <TextField name="phone" type="tel" label="Phone or WhatsApp" hint="Optional" autoComplete="tel" maxLength={40} className={TALL} />
              <TextField name="firmName" label="Firm or company" hint="Optional" autoComplete="organization" maxLength={200} className={TALL} />
              <Choices legend="You are" options={INTEREST_ROLES} chosen={new Set(role ? [role] : [])} onToggle={(k) => setRole((r) => (r === k ? null : k))} />
              <Choices legend="Size of your firm or team" options={INTEREST_FIRM_SIZES} chosen={new Set(size ? [size] : [])} onToggle={(k) => setSize((s) => (s === k ? null : k))} />
              <Choices legend="What would you like Leveri to do for you?" hint="Choose as many as you like." options={INTEREST_WANTS} chosen={wants} onToggle={toggleWant} />
              <TextField name="wantsOther" label="Anything else, in your own words" hint="Optional" maxLength={1000} className={TALL} />
              {/* A field people never see: a form that fills it in is not a person. */}
              <input name="website" type="text" tabIndex={-1} autoComplete="off" aria-hidden className="absolute -left-[9999px] h-px w-px opacity-0" />
              <label className="flex items-start gap-2.5 text-[14px] leading-snug text-ink-soft">
                <input type="checkbox" name="consent" required className="mt-0.5 h-4 w-4 shrink-0 accent-forest" />
                <span>Leveri may contact me about my invitation. My details are kept only for that and are not shared.</span>
              </label>
              {failure && <Notice>{failure}</Notice>}
              <Button type="submit" disabled={busy} className="!h-11 w-full !text-[15px]" data-testid="register-submit">Register</Button>
              {busy && <Reading>Registering…</Reading>}
            </form>
          </>
        )}
      </div>
    </main>
  );
}
